/**
 * Cache update helper for the `saveContractExtraction` mutation (SE-4819).
 *
 * Replaces the prior `refetchQueries: [{ query: PLAYER_CONTRACTS_OVERVIEW_QUERY }]`
 * approach which sometimes failed to flip the matching row's status cell to the
 * SAVED green-checkmark state — the user had to manually refresh. Writing the
 * narrow status delta directly into the cached `playerContractsOverview` query
 * is both faster (no network round trip) and reliably reactive: Apollo notifies
 * every observer that depends on the rewritten cache cell synchronously.
 *
 * Mirrors the existing pattern in `useArchiveContractExtraction` which already
 * mutates the same Active-list cache entry after archive.
 * @module features/contracts/hooks/applyContractExtractionSaveToCache
 */

import type { ApolloCache } from "@apollo/client";

import {
  ContractExtractionStatus,
  DocumentType,
  PlayerContractsOverviewDocument,
  type PlayerContractsOverviewQuery,
} from "@/generated/graphql";

/** Cached `playerContractsOverview` payload shape (root level of the query). */
type Overview = PlayerContractsOverviewQuery["playerContractsOverview"];

/** A single row inside the cached `players` list. */
type Row = Overview["players"][number];

/** Arguments accepted by {@link applyContractExtractionSaveToCache}. */
export interface ApplyContractExtractionSaveArgs {
  /** UUID of the player whose row should flip to SAVED. */
  readonly playerId: string;
  /**
   * Document type whose status field should flip. Accepted as a plain
   * string so callers don't need to import the codegen enum at the call
   * site (it crosses a `string` typed boundary in the hook signature).
   */
  readonly documentType: string;
  /**
   * Server-stamped `savedAt` timestamp from the mutation response.
   * Drives the row's `lastUpdated` field so the table's "Last updated"
   * column updates in lockstep with the status cell. Null when the
   * server omitted the field (defensive — it's typed nullable).
   */
  readonly savedAt: string | null;
}

/** Per-tier completeness bucket keys (loanee intentionally excluded — see below). */
type BucketKey = "firstTeamCompleteness" | "academyCompleteness";

/** Counter keys inside a {@link BucketKey} bucket. */
type CounterKey = "transfersSaved" | "contractsSaved";

/**
 * Resolves the completeness bucket key for a player's tier.
 * Returns `null` for unrecognised tiers (e.g. loanee — not exposed on the row).
 * @param tier - `tier` field from a `PlayerContractRow`
 * @returns Bucket key or `null` when the tier doesn't map to one
 */
function bucketKeyForTier(tier: string): BucketKey | null {
  if (tier === "First Team") return "firstTeamCompleteness";
  if (tier === "Academy") return "academyCompleteness";
  return null;
}

/**
 * Builds the next {@link Row} with the SAVED delta applied to the right
 * status field plus a refreshed `lastUpdated` value.
 * @param target - The current cached row for this player
 * @param isTransferAgreement - Whether to flip `transferStatus` (else `contractStatus`)
 * @param savedAt - Server-stamped save timestamp (falls back to existing value)
 * @returns The new immutable row to splice into the players list
 */
function buildUpdatedRow(
  target: Row,
  isTransferAgreement: boolean,
  savedAt: string | null
): Row {
  const statusPatch = isTransferAgreement
    ? { transferStatus: ContractExtractionStatus.Saved }
    : { contractStatus: ContractExtractionStatus.Saved };
  return {
    ...target,
    ...statusPatch,
    lastUpdated: savedAt ?? target.lastUpdated,
  };
}

/**
 * Increments the per-tier saved counter inside the relevant completeness
 * bucket. Returns the original overview unchanged when (a) the row's tier
 * does not map to a tracked bucket (e.g. loanee), or (b) the row was
 * already SAVED — incrementing again would double-count.
 * @param overview - The post-row-update overview
 * @param target - The original (pre-update) row, source of `tier`
 * @param wasAlreadySaved - Skip the bump when true
 * @param isTransferAgreement - Picks the `transfersSaved` vs `contractsSaved` counter
 * @returns Overview with the incremented counter (or unchanged when no-op)
 */
function bumpTierCompleteness(
  overview: Overview,
  target: Row,
  wasAlreadySaved: boolean,
  isTransferAgreement: boolean
): Overview {
  if (wasAlreadySaved) return overview;
  const bucketKey = bucketKeyForTier(target.tier);
  if (!bucketKey) return overview;

  const counterKey: CounterKey = isTransferAgreement
    ? "transfersSaved"
    : "contractsSaved";
  const bucket = overview[bucketKey];
  return {
    ...overview,
    [bucketKey]: {
      ...bucket,
      [counterKey]: bucket[counterKey] + 1,
    },
  };
}

/**
 * Applies the SAVED status delta from a `saveContractExtraction` mutation
 * to the cached `playerContractsOverview(archivedOnly: false)` query.
 *
 * No-op when the active-list query has not been read yet (no cache entry
 * to update), when the matching player is not in the active list (e.g.
 * the user navigated to the drawer from a different surface), or when
 * the document type is not one of the recognised values. The Archived
 * list is intentionally left untouched — saved rows live on the Active
 * list, so no archived-cache write is needed.
 * @param cache - Apollo cache instance from the mutation `update` callback
 * @param args - Save delta to apply
 * @param args.playerId - UUID of the player whose row should flip to SAVED
 * @param args.documentType - `CONTRACT` or `TRANSFER_AGREEMENT`; other values are ignored
 * @param args.savedAt - Server-stamped save timestamp to copy to `lastUpdated`
 */
export function applyContractExtractionSaveToCache(
  cache: ApolloCache<unknown>,
  { playerId, documentType, savedAt }: ApplyContractExtractionSaveArgs
): void {
  const isTransferAgreement = documentType === DocumentType.TransferAgreement;
  const isContract = documentType === DocumentType.Contract;
  if (!isTransferAgreement && !isContract) return;

  const existing = cache.readQuery<PlayerContractsOverviewQuery>({
    query: PlayerContractsOverviewDocument,
    variables: { archivedOnly: false },
  });
  if (!existing) return;

  const overview = existing.playerContractsOverview;
  const targetIndex = overview.players.findIndex(p => p.playerId === playerId);
  if (targetIndex < 0) return;

  const target = overview.players[targetIndex];
  const previousStatus = isTransferAgreement
    ? target.transferStatus
    : target.contractStatus;
  const wasAlreadySaved = previousStatus === ContractExtractionStatus.Saved;

  const updatedRow = buildUpdatedRow(target, isTransferAgreement, savedAt);
  const players = overview.players.map((p, i) =>
    i === targetIndex ? updatedRow : p
  );

  const overviewWithRow: Overview = { ...overview, players };
  const nextOverview = bumpTierCompleteness(
    overviewWithRow,
    target,
    wasAlreadySaved,
    isTransferAgreement
  );

  cache.writeQuery<PlayerContractsOverviewQuery>({
    query: PlayerContractsOverviewDocument,
    variables: { archivedOnly: false },
    data: { playerContractsOverview: nextOverview },
  });
}
